import { Injectable } from "@angular/core";
import { WebService } from "@app-cfs/core/services";
import { CfsTemplateService } from "@app-cfs/shared/services/cfs-template.service";
import { SettingsHandlerService } from "@app-cfs/shared/services/settings-handler.service";
import { TemplatesService } from "@app-cfs/shared/services/templates.service";
import { TranslationService } from "@app-cfs/shared/services/translation.service";
import { TranslocoService } from "@ngneat/transloco";
import { CFSCarouselDisplayOptions, CFSConnectionMessage, DisplayQrCodeInfo, ShowCfsTemplateInfo } from "cfs-communication-pack";
import {
  ApplicationStylesService,
  CfsMenuDto,
  CfsService,
  DeliverySource,
  DisplayQrCodeLogoTypes,
  GlobalTemplateType,
  TemplateImageSourceType,
  TemplateLogoSourceType
} from "clearline-api";
import { CfsTemplateError, generateErrorTypeText, isImportedVideoTemplate, isYoutubeTemplate, Template } from "clearline-common";
import { AppConfigService, LocalStorageService, UserAgentService } from "core-kit";
import { BehaviorSubject, combineLatest, Observable, of, Subject } from "rxjs";
import { catchError, distinctUntilChanged, filter, map, mergeMap, switchMap, tap } from "rxjs/operators";
import { ButtonViewType, CarouselUserAction, CarouselUserActionType, TemplatesCarouselData } from "../models";
import { ConsoleLogHandlerService } from "@app-cfs/shared/services/console-log-handler.service";
import { CarouselSkipSlideService } from "@app-cfs/shared/services/carousel-skip-slide.service";
import { StateService } from "@app-cfs/shared/services/state.service";
import { StorageKeys } from "@app-cfs/shared/models/storage-keys";

export interface CarouselSlideInfo extends ShowCfsTemplateInfo {
  template: Template;
}

export interface RefreshableCarouselTemplate extends Template {
  refreshId?: number;
}

export interface CarouselStateInfo {
  isLoaded: boolean;
  isPlaying: boolean;
  isPaused: boolean;
  hasSkippedSlides: boolean;
  skippedSlidesCount: number;
  currentSlideIndex: number;
  isTestMode: boolean;
  totalSlides: number;
}

@Injectable({
  providedIn: "root"
})
export class CarouselService {
  constructor(
    private applicationStylesService: ApplicationStylesService,
    private settingsHandler: SettingsHandlerService,
    private templatesService: TemplatesService,
    private cfsTemplateService: CfsTemplateService,
    private cfsService: CfsService,
    private translationService: TranslationService,
    private translateService: TranslocoService,
    private webService: WebService,
    private localStorageService: LocalStorageService,
    private appConfigService: AppConfigService,
    private logService: ConsoleLogHandlerService,
    private skipSlideService: CarouselSkipSlideService,
    private stateService: StateService,
    private userAgentService: UserAgentService
  ) {
    if (this.isProblematicBrowserForYouTube) {
      console.log("Problematic browser version for YouTube");
    }
  }

  private readonly _defaultSlideIndex = 0;
  readonly isProblematicBrowserForYouTube: boolean = this.userAgentService.isProblematicBrowserForYouTube();

  private _savedSlideIndex = this._defaultSlideIndex;
  private templateList: Template[] | null = null;
  private preparedTemplateList: Template[] | null = null;
  private forcedSlideQueue: CarouselSlideInfo[] = [];
  private isAutoplayEnabled: boolean = false;

  private displayLoaderSplashScreenSrc$ = new BehaviorSubject<boolean>(false);
  readonly displayLoaderSplashScreen$ = this.displayLoaderSplashScreenSrc$.asObservable();

  private displayNoTemplatesSplashScreenSrc$ = new BehaviorSubject<boolean>(false);
  readonly displayNoTemplatesSplashScreen$ = this.displayNoTemplatesSplashScreenSrc$.asObservable();

  readonly templateIdSkipList$ = this.skipSlideService.slideSkipList$;

  private slideRemainingTimeSrc$ = new BehaviorSubject<number>(0);
  readonly slideRemainingTime$ = this.slideRemainingTimeSrc$.asObservable();

  private inProgress$ = new BehaviorSubject<boolean>(false);
  readonly isCarouselPlaying$ = this.inProgress$.pipe(distinctUntilChanged());

  private currentData = new BehaviorSubject<TemplatesCarouselData | null>(null);
  readonly currentData$: Observable<TemplatesCarouselData | null> = this.currentData.asObservable();

  private menuInfoSrc = new BehaviorSubject<CfsMenuDto | null>(null);
  readonly menuInfo$: Observable<CfsMenuDto | null> = this.menuInfoSrc.asObservable();

  private updateMenuSrc = new BehaviorSubject<boolean>(false);
  readonly updateMenu$ = this.updateMenuSrc.asObservable().pipe(filter((value) => value));

  private forcedSlideSessionExpiredSrc = new Subject<DisplayQrCodeInfo>();
  readonly forcedSlideSessionExpired$ = this.forcedSlideSessionExpiredSrc.asObservable();

  private forcedSlideSrc$ = new BehaviorSubject<RefreshableCarouselTemplate | null>(null);
  readonly forcedSlide$ = this.forcedSlideSrc$.asObservable();

  private resetCarouselSrc$ = new Subject<void>();
  readonly resetCarousel$ = this.resetCarouselSrc$.asObservable();

  private readonly hideForcedSlideSrc$ = new Subject();
  readonly hideForcedSlide$ = this.hideForcedSlideSrc$.asObservable();

  readonly isSuspended$ = this.stateService.isSuspended$;

  get isCarouselPlaying(): boolean {
    return this.inProgress$.value;
  }

  get isCarouselAutoplayEnabled(): boolean {
    return this.isAutoplayEnabled;
  }

  get slideRemainingTime(): number {
    return this.slideRemainingTimeSrc$.value;
  }

  get defaultSlideIndex(): number {
    return this._defaultSlideIndex;
  }

  get forcedSlide(): RefreshableCarouselTemplate | null {
    return this.forcedSlideSrc$.value;
  }

  get isAllTemplatesSkipped(): boolean {
    return this.skipSlideService.isAllSlidesSkipped;
  }

  get skippedTemplateIds(): number[] {
    return this.skipSlideService.skipList;
  }

  get isInteractionAllowed(): boolean {
    return this.stateService.isInteractionAllowed;
  }

  get isCarouselPlayAllowed(): boolean {
    return !this.stateService.isSuspended;
  }

  getCarouselStateInfo(): CarouselStateInfo {
    return {
      isLoaded: !!this.preparedTemplateList,
      isPlaying: this.isCarouselPlaying,
      isPaused: !this.isCarouselPlaying,
      hasSkippedSlides: this.skipSlideService.skipList.length > 0,
      skippedSlidesCount: this.skipSlideService.skipList.length,
      currentSlideIndex: this._savedSlideIndex,
      isTestMode: this.getIsTestMode(),
      totalSlides: this.preparedTemplateList?.length || 0
    };
  }

  setCarouselAutoplay(isAutoplayEnabled: boolean): void {
    this.isAutoplayEnabled = isAutoplayEnabled;
    this.inProgress$.next(isAutoplayEnabled);
  }

  saveSlideIndex(index: number): void {
    this._savedSlideIndex = index;
  }

  resetCarousel(): void {
    this.resetCarouselSrc$.next();
  }

  pauseCarousel(): void {
    this.logService.log("Pause carousel");
    this.inProgress$.next(false);
  }

  playCarousel(): void {
    // todo: check inner forced slide condition
    const isPlayAllowed: boolean = this.stateService.isCarouselPlayAllowed && !this.forcedSlide;

    if (isPlayAllowed) {
      this.logService.log("Play carousel");
      this.inProgress$.next(true);
    }
  }

  updateSlideRemainingTime(remainingTime: number): void {
    this.slideRemainingTimeSrc$.next(remainingTime);
  }

  sendUserAction(action: CarouselUserAction): void {
    this.stateService.sendUserAction(action);
  }

  runUpdateMenu(): void {
    this.updateMenuSrc.next(true);
  }

  hideForcedSlide(): void {
    if (this.forcedSlide) {
      this.hideForcedSlideSrc$.next();
    }
  }

  updateCarouselData(data: TemplatesCarouselData): void {
    this.currentData.next(data);
  }

  displayLoaderSplashScreen(): void {
    this.displayLoaderSplashScreenSrc$.next(true);
  }

  hideLoaderSplashScreen(): void {
    this.displayLoaderSplashScreenSrc$.next(false);
  }

  displayNoTemplatesSplashScreen(): void {
    this.hideLoaderSplashScreen();
    this.displayNoTemplatesSplashScreenSrc$.next(true);
  }

  hideNoTemplatesSplashScreen(): void {
    if (this.displayNoTemplatesSplashScreenSrc$.value) {
      this.displayNoTemplatesSplashScreenSrc$.next(false);
    }
  }

  setIsAllTemplatesSkipped(isAllSkipped: boolean): void {
    this.skipSlideService.setIsAllTemplatesSkipped(isAllSkipped);
    this.toggleSplashScreenOnAllSlidesSkip();
  }

  displayCarousel({ updateOnNewCircle = false, forceUpdate = false }: CFSCarouselDisplayOptions = {}): Observable<Template[]> {
    const templateList: Template[] = this.settingsHandler.templateList;
    const isListUpdated: boolean = JSON.stringify(templateList) !== JSON.stringify(this.templateList);

    if (isListUpdated || forceUpdate) {
      const preparedTemplateList$: Observable<Template[]> = this.getPreparedTemplateList();

      return preparedTemplateList$.pipe(
        tap((preparedTemplates: Template[]) => {
          this.templateList = templateList;
          this.preparedTemplateList = preparedTemplates.filter((template: Template, index: number) =>
            this.filterAndProcessTemplateError(template, index)
          );

          this.updateCarouselData({
            templateList: this.preparedTemplateList,
            updateOnNewCircle
          });
        })
      );
    }

    return of([]);
  }

  displayForcedSlide(slideInfo: ShowCfsTemplateInfo): Observable<Template[]> {
    const timeout: number = slideInfo.timeout || 0;

    return this.cfsTemplateService.getForceTemplateList(slideInfo).pipe(
      catchError(() => of(null)),
      mergeMap((templateList) => {
        if (!templateList?.length) return of([]);

        return this.templatesService.getPreparedTemplates$(templateList).pipe(
          tap((preparedTemplateList: Template[]) => {
            const preparedTemplate: Template | null = preparedTemplateList?.[0] || null;

            if (preparedTemplate && this.filterAndProcessTemplateError(preparedTemplate, 0) && timeout) {
              const newSlide: CarouselSlideInfo = this.createForcedSlideInfo(preparedTemplate, slideInfo);

              this.addToForcedSlideQueue(newSlide);
              this.pauseCarousel();

              if (!this.forcedSlide) {
                this.playNextForcedSlideFromQueue();
              }
            }
          })
        );
      })
    );
  }

  private createForcedSlideInfo(rawTemplate: Template, slideInfo: ShowCfsTemplateInfo): CarouselSlideInfo {
    const { timeout = 0, sessionId, widgetId } = slideInfo;
    const template: Template = { ...rawTemplate };
    const displayTime: number = timeout ? timeout * 1000 : 0;

    if (!template.configuration) {
      template.configuration = {};
    } else {
      if (!template.configuration.options) {
        template.configuration.options = {
          displayTime,
          image: { sourceType: TemplateImageSourceType.Default },
          logo: { sourceType: TemplateLogoSourceType.Default }
        };
      } else {
        template.configuration.options.displayTime = displayTime;
      }
    }

    return {
      template,
      timeout,
      sessionId,
      widgetId
    };
  }

  playNextForcedSlideFromQueue(): void {
    if (this.forcedSlideQueue.length > 0) {
      const nextSlideInfo = this.forcedSlideQueue[0];
      const { template } = nextSlideInfo;

      if (template) {
        this.setForcedSlide(template);
        this.logService.log(`Playing forced slide from queue. Remaining slides: ${this.forcedSlideQueue.length - 1}`);
      } else {
        this.forcedSlideQueue.shift();
        this.playNextForcedSlideFromQueue();
      }
    } else {
      this.clearForcedSlideQueue();
      this.playCarousel();
    }
  }

  onForcedSlideCompleted(sessionId?: string): void {
    this.removeCompletedSlideFromQueue(sessionId);

    if (!this.forcedSlideQueue.length) {
      this.finishForcedSlideDisplay();
      return;
    }

    if (!sessionId || !this.forcedSlide) {
      this.playNextForcedSlideFromQueue();
    }
  }

  onForcedCarouselTimeout(error?: CfsTemplateError): void {
    const currentSlideInfo: CarouselSlideInfo | null = this.getCurrentSlideInfo();
    const { sessionId, widgetId } = currentSlideInfo || {};

    if (sessionId) {
      const errorMessage: string = error ? this.generateCfsTemplateErrorMessage(error) : "";
      const info: DisplayQrCodeInfo = { sessionId, widgetId, error: errorMessage };
      this.logService.log("On forced slide timeout", currentSlideInfo);
      this.forcedSlideSessionExpiredSrc.next(info);
    }
  }

  onQrCodeScanned(message: CFSConnectionMessage): void {
    const { sessionId, deliverySource } = message.qrCodeScannedInfo || {};
    const qrCodeSlideInfo: CarouselSlideInfo | null = this.getForcedSlideBySessionId(sessionId);
    const currentTemplate: Template | undefined = qrCodeSlideInfo?.template;

    if (currentTemplate?.redirectSettings?.redirectPageUrl) {
      if (deliverySource === DeliverySource.Cfs) {
        const { redirectPageUrl, timeoutInSeconds } = currentTemplate.redirectSettings;
        const destinationUrl: string = this.webService.getFullUrl(redirectPageUrl);
        const timeoutRedirect: number = timeoutInSeconds ? timeoutInSeconds * 1000 : 0;
        this.sendUserAction({
          actionType: CarouselUserActionType.templateAction,
          redirectUrl: destinationUrl,
          timeoutRedirect,
          confirmActivity: false,
          trackActivity: false,
          closeBtnViewType: ButtonViewType.Cross
        });
      }

      this.onForcedSlideCompleted(sessionId);
    }
  }

  onQrCodeHidden(message: CFSConnectionMessage): void {
    const { sessionId } = message.hideQrCodeInfo || {};
    const qrCodeSlideInfo: CarouselSlideInfo | null = this.getForcedSlideBySessionId(sessionId);

    if (qrCodeSlideInfo) {
      this.sendUserAction({ actionType: CarouselUserActionType.goBack });
      this.onForcedSlideCompleted(sessionId);
    }
  }

  setSlideSuccessDisplay(template: Template): void {
    this.skipSlideService.setSlideSuccessDisplay(template);
  }

  setSlideError(template: Template, index: number, error: CfsTemplateError): void {
    this.skipSlideService.setSlideError(template, index, error);
  }

  isSlidePreloadable(templateList: Template[] = [], slideIndex: number): boolean {
    const template: Template | null = templateList[slideIndex] || null;

    if (!this.isProblematicBrowserForYouTube) {
      return true;
    }

    const isYouTubeTemplate: boolean = isYoutubeTemplate(template);

    return !isYouTubeTemplate;
  }

  shouldTriggerChDToPlaySlide(template: Template): boolean {
    return isImportedVideoTemplate(template);
  }

  isTemplateShouldBeSkipped(templateId: number): boolean {
    return this.skipSlideService.isSlideShouldBeSkipped(templateId);
  }

  updateAllTemplatesSkippedState(templateList: Template[] = []): void {
    this.skipSlideService.updateAllTemplatesSkippedState(templateList);
    this.toggleSplashScreenOnAllSlidesSkip();
  }

  getCurrentSlideInfo(): CarouselSlideInfo | null {
    if (this.forcedSlideQueue.length > 0) {
      return this.forcedSlideQueue[0];
    }

    const mainTemplate: Template | null = this.templateList?.[this._savedSlideIndex] || null;
    return mainTemplate ? { template: mainTemplate } : null;
  }

  updateCfsMenuList(): Observable<null> {
    const { locationId, cashRegister } = this.settingsHandler;

    return this.cfsService.getCfsMenuList(locationId, cashRegister).pipe(
      tap((menuInfo: CfsMenuDto) => {
        this.menuInfoSrc.next(menuInfo);
      }),
      switchMap(({ brand }) =>
        this.applicationStylesService.getByBrandId(brand.id).pipe(
          tap((styles) => {
            if (styles) {
              const getQrLogoSettings = (qrLogoType: DisplayQrCodeLogoTypes) => {
                switch (qrLogoType) {
                  case DisplayQrCodeLogoTypes.BrandLogo:
                    return brand.logo;
                  case DisplayQrCodeLogoTypes.CustomLogo:
                    return styles.qrConfiguration.logo;
                  default:
                    return "";
                }
              };
              const { qrConfiguration } = styles;
              const updatedQrConfiguration = { ...qrConfiguration, logo: getQrLogoSettings(qrConfiguration.qrLogoType) }; // fixme: remove after fix ApplicationStyles on backend side

              this.settingsHandler.setApplicationStyles({ ...styles, qrConfiguration: updatedQrConfiguration });
            }
          })
        )
      ),
      map(() => null),
      catchError(() => of(null))
    );
  }

  generateNoTemplatesSplashScreen(): Template {
    return this.generateSplashScreen(false, true);
  }

  generateLoaderSplashScreen(): Template {
    return this.generateSplashScreen(true, true);
  }

  getIsTestMode(): boolean {
    return !this.appConfigService.isProduction && this.localStorageService.getItem(StorageKeys.testCarouselMode) === "true";
  }

  clearPrevData(): void {
    this.skipSlideService.clearData();
  }

  clearForcedSlideQueue(): void {
    this.forcedSlideQueue = [];
    this.clearForcedSlide();
  }

  private clearForcedSlide(): void {
    this.forcedSlideSrc$.next(null);
  }

  private addToForcedSlideQueue(slideInfo: CarouselSlideInfo): void {
    this.forcedSlideQueue.push(slideInfo);
    this.logService.log(`Added forced slide to queue. Queue length: ${this.forcedSlideQueue.length}`);
  }

  private setForcedSlide(template: Template): void {
    this.forcedSlideSrc$.next({ ...template, refreshId: Date.now() });
  }

  private removeCompletedSlideFromQueue(sessionId?: string): void {
    if (!this.forcedSlideQueue.length) {
      return;
    }

    if (sessionId) {
      this.forcedSlideQueue = this.forcedSlideQueue.filter((slide) => slide.sessionId !== sessionId);
    } else {
      this.forcedSlideQueue.shift();
    }

    this.clearForcedSlide();
    this.logService.log(`Removed completed forced slide from queue. Remaining slides: ${this.forcedSlideQueue.length}`);
  }

  private getForcedSlideBySessionId(sessionId?: string): CarouselSlideInfo | null {
    if (!sessionId) {
      return null;
    }
    return this.forcedSlideQueue.find((slide) => slide.sessionId === sessionId) || null;
  }

  private filterAndProcessTemplateError(template: Template, index: number = 0): boolean {
    const filterError: CfsTemplateError | null = this.templatesService.getTemplateFilterError(template);

    if (filterError) {
      this.setSlideError(template, index, filterError);
      return false;
    }

    return true;
  }

  private toggleSplashScreenOnAllSlidesSkip(): void {
    if (this.skipSlideService.isAllSlidesSkipped) {
      this.displayNoTemplatesSplashScreen();
    } else {
      this.hideNoTemplatesSplashScreen();
    }
  }

  private finishForcedSlideDisplay(): void {
    this.logService.log("Finish forced slide display");
    this.clearForcedSlide();
    this.hideLoaderSplashScreen();

    if (this.forcedSlideQueue.length === 0) {
      this.playCarousel();
    }
  }

  private getPreparedTemplateList(): Observable<Template[]> {
    const templateList: Template[] = this.settingsHandler.templateList;

    if (this.isCarouselPrepared(templateList)) {
      return of(this.preparedTemplateList as Template[]);
    }

    return combineLatest([
      this.templatesService.getPreparedTemplates$(templateList?.length ? templateList : []),
      this.translationService.ready$
    ]).pipe(map(([preparedTemplates]) => preparedTemplates));
  }

  private isCarouselPrepared(templateList: Template[]): boolean {
    if (this.preparedTemplateList?.length) {
      const getTemplateWithPreviewReset = (item: Template) => {
        return { ...item, previewOptions: undefined };
      };
      const handledTemplateList: Template[] = templateList.map(getTemplateWithPreviewReset);
      const handledPreparedTemplateList: Template[] = this.preparedTemplateList.map(getTemplateWithPreviewReset);

      return JSON.stringify(handledTemplateList) === JSON.stringify(handledPreparedTemplateList);
    }

    return false;
  }

  private generateSplashScreen(hasTemplates = false, isFullScreenForced = false): Template {
    const contentWithLoading: boolean = hasTemplates;
    const fullScreenCssClass = isFullScreenForced || !this.getIsTestMode() ? "full-screen" : "";

    const loadingGeneratedBody = `<div class="template generatedHtml splash-screen animated-splash-screen ${fullScreenCssClass}">
      <div class="splash-screen__content">
        <img alt="" src="assets/images/splash-screen.svg" class="splash-screen__img">

        <span class="loader"></span>
      </div>
    </div>`;

    const noTemplatesGeneratedBody = `<div class="template generatedHtml splash-screen ${fullScreenCssClass}">
      <div class="splash-screen__content">
        <img alt="" src="assets/images/empty-carousel.svg" class="splash-screen__img">

        <p class="splash-screen__text">${this.translateService.translate("splashScreen.noTemplates")}</p>
      </div>
    </div>`;

    const generatedBody = contentWithLoading ? loadingGeneratedBody : noTemplatesGeneratedBody;

    return {
      id: 0,
      templateId: 0,
      name: "Splash Screen",
      globalTemplateType: GlobalTemplateType.Custom,
      enabled: true,
      generatedBody,
      isSplashScreen: true
    };
  }

  private generateCfsTemplateErrorMessage(error: CfsTemplateError): string {
    if (!error) {
      return "";
    }

    const translationKey = "error.cfsTemplateLoad";
    const mainMessage = this.translateService.translateObject(`${translationKey}.mainMsg`);
    const restMessage: string = generateErrorTypeText(error, false)?.toLowerCase();

    if (restMessage) {
      return `${mainMessage} ${this.translateService.translateObject(`${translationKey}.dueTo`, { reason: restMessage })}`;
    }

    return mainMessage;
  }
}
